import axios from 'axios';
// eslint-disable-next-line import/no-cycle, import/extensions
import { handleRequests, createCancellationTokenSource } from '@/helpers/index';
// eslint-disable-next-line import/extensions, import/no-cycle
import { ISSUE_STATUSES, FEATURE_NAMES } from '@/utils/constants';
// eslint-disable-next-line import/extensions
import i18n from '@/i18n';
import {
    FETCH_TEAM_MEMBERS,
    FETCH_AVAILABLE_TEAM_MEMBERS,
    UPDATE_TEAM_MEMBERS,
    FETCH_TEAM_INVITES,
    UPDATE_TEAM_INVITES,
    FETCH_TEAM_REGIONS,
    SET_TEAM_REGIONS,
    FETCH_WAREHOUSES,
    SET_WAREHOUSES,
    FETCH_TEAM_CARRIERS,
    SET_TEAM_CARRIERS,
    FETCH_TEAM_CUSTOMERS,
    SET_TEAM_CUSTOMERS,
    FETCH_TEAM_HAS_CUSTOMERS,
    SET_TEAM_HAS_CUSTOMERS,
    UPDATE_SKILLS,
    FETCH_SKILLS,
    SET_INVOICE_ADJUSTMENT_CATEGORIES,
    FETCH_INVOICE_ADJUSTMENT_CATEGORIES,
    SET_INVOICE_ADJUSTMENT_STATUSES,
    FETCH_INVOICE_ADJUSTMENT_STATUSES,
    FETCH_PENDING_ISSUES,
    SET_PENDING_ISSUES,
    INCREMENT_PENDING_ISSUES,
    DECREMENT_PENDING_ISSUES
} from '../types';

const state = {
    teamMembers: [],
    inviteList: [],
    teamRegions: [],
    warehouses: [],
    teamCarriers: [],
    customers: [],
    hasCustomers: false,
    skills: [],
    invoiceAdjustmentStatuses: [],
    invoiceAdjustmentCategories: [],
    pendingIssues: 0,
    fetchTeamMembersCancellationTokenSource: null,
    lastFetchTeamMembersKey: null,
    isFetchingTeamMembers: false
};

const getters = {
    teamMembers: (state) => state.teamMembers,
    isSingleTeamMember: (state) => state.teamMembers.length <= 1,
    teamRegions: (state) => state.teamRegions,
    warehouses: (state) => state.warehouses,
    teamStatus: (state, getters, rootState) => {
        return rootState.user.teamStatus;
    },
    isTrialExpired: (state, getters, rootState) => {
        return !(
            rootState.user.teamStatus === 'Unknown' ||
            rootState.user.teamStatus === 'Active' ||
            rootState.user.teamStatus === 'Disabled?'
        );
    },
    teamCarriers: (state) => state.teamCarriers,
    hasActiveCarriers: (state) => {
        if (state.teamCarriers == null || state.teamCarriers.length === 0) 
            return false;

        const activeCarriers = state.teamCarriers.filter((x) => x.status === 'Active');

        return activeCarriers.length > 0;
    },
    activeCarriers: (state) => (!state.teamCarriers ? [] : state.teamCarriers.filter((x) => x.status === 'Active')),
    customers: (state) => state.customers,
    hasCustomers: (state) => state.hasCustomers,
    skills: (state) => state.skills,
    invoiceAdjustmentStatuses: (state) => [],
    invoiceAdjustmentCategories: (state) => [],
    pendingIssues: (state) => state.pendingIssues
};

const mutations = {
    [UPDATE_TEAM_MEMBERS]: (state, payload) => {
        state.teamMembers = payload;
    },
    [UPDATE_TEAM_INVITES]: (state, payload) => {
        state.inviteList = payload;
    },
    [SET_TEAM_REGIONS]: (state, payload) => {
        state.teamRegions = [...payload];
    },
    [SET_WAREHOUSES]: (state, payload) => {
        state.warehouses = [...payload];
    },
    [SET_TEAM_CARRIERS]: (state, payload) => {
        state.teamCarriers = [...payload];
    },
    [SET_TEAM_CUSTOMERS]: (state, payload) => {
        state.customers = [...payload];
    },
    [SET_TEAM_HAS_CUSTOMERS]: (state, payload) => {
        state.hasCustomers = payload;
    },
    [UPDATE_SKILLS]: (state, payload) => {
        state.skills = payload;
    },
    [SET_INVOICE_ADJUSTMENT_CATEGORIES]: (state, payload) => {
        state.invoiceAdjustmentCategories = payload;
    },
    [SET_INVOICE_ADJUSTMENT_STATUSES]: (state, payload) => {
        state.invoiceAdjustmentStatuses = payload;
    },
    [SET_PENDING_ISSUES]: (state, payload) => {
        state.pendingIssues = payload;
    },
    [INCREMENT_PENDING_ISSUES]: (state) => {
        state.pendingIssues += 1;
    },
    [DECREMENT_PENDING_ISSUES]: (state) => {
        if (state.pendingIssues > 0) {
            state.pendingIssues -= 1;
        }
    }
};

const actions = {
    async [FETCH_AVAILABLE_TEAM_MEMBERS]({ state }, { appointmentDate }) {
        const teamMembers = state.teamMembers?.map((x) => x.publicUserId);
        if (!teamMembers || teamMembers.length === 0) 
            return [];

        const url = `/api/team-members/available?appointmentDate=${appointmentDate}`;
        const payload = {
            method: 'post',
            data: teamMembers
        };
        const {
            data: { data: idListOfAvailableTeamMembers }
        } = await handleRequests(url, payload);
        return idListOfAvailableTeamMembers;
    },
    async [FETCH_TEAM_MEMBERS](
        { commit, state, rootGetters },
        payload = {
            date: '',
            includeLocationDetails: false,
            includeTripDetails: false,
            teamRegionId: null,
            runNumber: null,
            strictTeamRegionFilter: true,
            elevatedTeamRegionFilter: false
        }
    ) {
        const key = JSON.stringify(payload);
        if (state.isFetchingTeamMembers && key === state.lastFetchTeamMembersKey) {
            return state.teamMembers; // reuse existing
        }
        state.lastFetchTeamMembersKey = key;
        state.isFetchingTeamMembers = true;

        // Cancel previous request
        if (state.fetchTeamMembersCancellationTokenSource) {
            state.fetchTeamMembersCancellationTokenSource.cancel('New request initiated');
            state.fetchTeamMembersCancellationTokenSource = null;
        }

        state.fetchTeamMembersCancellationTokenSource = createCancellationTokenSource();

        try {
            const {
                date,
                includeLocationDetails,
                includeTripDetails,
                teamRegionId,
                runNumber,
                strictTeamRegionFilter,
                elevatedTeamRegionFilter
            } = payload;
            const url = `/api/team-members?date=${date}&includeLocationDetails=${includeLocationDetails}&includeTripDetails=${includeTripDetails}&teamRegionId=${teamRegionId}&strictTeamRegionFilter=${strictTeamRegionFilter}&elevatedTeamRegionFilter=${elevatedTeamRegionFilter}&runNumber=${runNumber}`;
            const { data } = await handleRequests(
                url,
                { method: 'GET' },
                true,
                state.fetchTeamMembersCancellationTokenSource.token
            );

            if (!data) {
                // Request was cancelled
                return null;
            }

            const response = data.data;
            const hasMemberCodeFeature = rootGetters['user/user']?.features?.includes(FEATURE_NAMES.MemberCodeDisplay);
            const mapped = Array.isArray(response)
                ? response.map((m) => {
                    const code = m.memberCode || null;
                    return hasMemberCodeFeature ? { ...m, memberCode: code } : { ...m, memberCode: null };
                })
                : response;
            commit(UPDATE_TEAM_MEMBERS, mapped);
            return mapped;
        } catch (e) {
            if (axios.isCancel && axios.isCancel(e)) {
                // Swallow cancellation errors
                return null;
            }
            throw e;
        } finally {
            state.fetchTeamMembersCancellationTokenSource = null;
            state.isFetchingTeamMembers = false;
        }
    },
    async [FETCH_TEAM_INVITES]({ commit }) {
        let loader = {
            isLoading: true,
            loadingText: i18n.t('common.loadingText')
        };
        commit('CHANGE_LOADING_STATE', loader, { root: true });
        const url = '/api/team-members/invite';
        const { data } = await handleRequests(url);

        loader = {
            isLoading: false,
            loadingText: i18n.t('common.loadingText')
        };
        commit('CHANGE_LOADING_STATE', loader, { root: true });
        commit(UPDATE_TEAM_INVITES, data.data);
        return data.data;
    },
    async [FETCH_TEAM_REGIONS]({ commit }) {
        const url = '/api/teams/team-regions';
        const { data } = await handleRequests(url);
        commit(SET_TEAM_REGIONS, data);
        return data;
    },
    async [FETCH_WAREHOUSES]({ commit }) {
        const url = `/api/warehouses?teamRegionId=&itemsPerPage=999`; // we are fetching all
        try {
            const { data } = await handleRequests(url);
            if (data && data.warehouses) {
                commit(SET_WAREHOUSES, [...data.warehouses]);
                return data.warehouses;
            }
            commit(SET_WAREHOUSES, []);
            return [];
        } catch (e) {
            commit(SET_WAREHOUSES, []);
            return [];
        }
    },
    async [FETCH_TEAM_CARRIERS]({ commit, rootGetters }) {
        if (rootGetters['user/hasMarketplaceAccess']) {
            const url = '/api/teams/carriers/list';
            const { data } = await handleRequests(url);
            commit(SET_TEAM_CARRIERS, data?.carriers || []);
            return data;
        }
        return [];
    },
    async [FETCH_TEAM_CUSTOMERS]({ commit }) {
        const url = '/api/customers/lookup';
        const { data } = await handleRequests(url);
        commit(SET_TEAM_CUSTOMERS, data?.items || []);
        return data;
    },
    async [FETCH_TEAM_HAS_CUSTOMERS]({ commit }) {
        const url = '/api/customers/hasAny';
        const { data } = await handleRequests(url);
        commit(SET_TEAM_HAS_CUSTOMERS, data || false);
        return data;
    },
    async [FETCH_SKILLS]({ commit }) {
        const url = '/api/teams/skills';
        const { data } = await handleRequests(url);
        if (data !== null) {
            commit(UPDATE_SKILLS, data.data || []);
        } else {
            commit(UPDATE_SKILLS, []);
        }
        return data;
    },
    async [FETCH_INVOICE_ADJUSTMENT_STATUSES]({ commit }) {
        const url = '/api/invoice-adjustments/statuses';
        const { data } = await handleRequests(url);
        commit(SET_INVOICE_ADJUSTMENT_STATUSES, data || false);
        return data;
    },
    async [FETCH_INVOICE_ADJUSTMENT_CATEGORIES]({ commit }) {
        const url = '/api/invoice-adjustments/categories';
        const { data } = await handleRequests(url);
        commit(SET_INVOICE_ADJUSTMENT_CATEGORIES, data || false);
        return data;
    },
    async [FETCH_PENDING_ISSUES]({ commit, rootGetters }) {
        try {
            const user = rootGetters['user/user'];
            if (user.features && user.features.includes(FEATURE_NAMES.IssueTracking)) {
                const url = '/api/issue/issues';
                const payload = {
                    method: 'get',
                    params: {
                        issueStatusId: ISSUE_STATUSES[0].issueStatusId
                    }
                };

                const data = await handleRequests(url, payload);
                commit(SET_PENDING_ISSUES, data.data.totalRecordCount);
                return data.data.totalRecordCount;
            }

            commit(SET_PENDING_ISSUES, 0);
            return 0;
        } catch (e) {
            commit(SET_PENDING_ISSUES, 0);
            return 0;
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
